import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  AppUser,
  UserRole,
  signInWithEmail,
  signUpWithEmail,
  signInWithGoogle,
  signOutUser,
  sendVerificationEmail,
  reloadUser,
  subscribeToAuthChanges,
  resetPassword,
} from '../services/authService';
import { isAdminUser, isKnownAdminEmail } from '../utils/adminAccess';

export type User = AppUser;

export interface AuthContextType {
  isAuthenticated: boolean;
  isAdmin: boolean;
  user: User | null;
  token: string | null;
  role: UserRole;
  login: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName?: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  reloadUser: () => Promise<AppUser | null>;
  resetPassword: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const INACTIVITY_TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 hours of inactivity
const MAX_SESSION_DURATION_MS = 12 * 60 * 60 * 1000; // 12 hours absolute maximum session lifetime
const ACTIVITY_STORAGE_KEY = 'ns_last_activity';
const SESSION_START_KEY = 'ns_session_start';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Subscribe to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges((firebaseUser, idToken) => {
      setUser(firebaseUser);
      setToken(idToken);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Track user activity and enforce 2h inactivity timeout + 12h max session limit
  useEffect(() => {
    if (!user) {
      return;
    }

    const now = Date.now();
    try {
      if (!localStorage.getItem(ACTIVITY_STORAGE_KEY)) {
        localStorage.setItem(ACTIVITY_STORAGE_KEY, String(now));
      }
      if (!localStorage.getItem(SESSION_START_KEY)) {
        localStorage.setItem(SESSION_START_KEY, String(now));
      }
    } catch {
      // ignore storage errors
    }

    let lastRecorded = Date.now();
    const handleUserActivity = () => {
      const currentTime = Date.now();
      // Throttle activity timestamp writes to once every 30 seconds
      if (currentTime - lastRecorded > 30000) {
        lastRecorded = currentTime;
        try {
          localStorage.setItem(ACTIVITY_STORAGE_KEY, String(currentTime));
        } catch {
          // ignore
        }
      }
    };

    const checkSessionExpiration = () => {
      try {
        const currentTime = Date.now();

        // 1. Check absolute maximum session limit (12 hours)
        const storedSessionStart = localStorage.getItem(SESSION_START_KEY);
        if (storedSessionStart) {
          const sessionStart = parseInt(storedSessionStart, 10);
          if (!isNaN(sessionStart) && currentTime - sessionStart > MAX_SESSION_DURATION_MS) {
            logout();
            try {
              localStorage.removeItem(ACTIVITY_STORAGE_KEY);
              localStorage.removeItem(SESSION_START_KEY);
            } catch {
              // ignore
            }
            window.location.href = '/login?session_expired=max_limit';
            return;
          }
        } else {
          localStorage.setItem(SESSION_START_KEY, String(currentTime));
        }

        // 2. Check idle inactivity timeout (2 hours)
        const storedActivityTime = localStorage.getItem(ACTIVITY_STORAGE_KEY);
        if (storedActivityTime) {
          const lastActive = parseInt(storedActivityTime, 10);
          if (!isNaN(lastActive) && currentTime - lastActive > INACTIVITY_TIMEOUT_MS) {
            logout();
            try {
              localStorage.removeItem(ACTIVITY_STORAGE_KEY);
              localStorage.removeItem(SESSION_START_KEY);
            } catch {
              // ignore
            }
            window.location.href = '/login?session_expired=inactivity';
            return;
          }
        } else {
          localStorage.setItem(ACTIVITY_STORAGE_KEY, String(currentTime));
        }
      } catch {
        // ignore
      }
    };

    // Check session on mount / user state change
    checkSessionExpiration();

    // Check session every 60 seconds
    const intervalId = setInterval(checkSessionExpiration, 60000);

    // Also check when tab becomes visible or window gains focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSessionExpiration();
      }
    };

    const handleStorageChange = (e: StorageEvent) => {
      if ((e.key === ACTIVITY_STORAGE_KEY || e.key === SESSION_START_KEY) && !e.newValue) {
        setUser(null);
        setToken(null);
      }
    };

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', checkSessionExpiration);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(intervalId);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', checkSessionExpiration);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [user]);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const authenticatedUser = await signInWithEmail(email, password);
      setUser(authenticatedUser);
      const now = String(Date.now());
      try {
        localStorage.setItem(ACTIVITY_STORAGE_KEY, now);
        localStorage.setItem(SESSION_START_KEY, now);
      } catch {
        // ignore
      }
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, displayName?: string) => {
    setLoading(true);
    try {
      const newUser = await signUpWithEmail(email, password, displayName);
      setUser(newUser);
      const now = String(Date.now());
      try {
        localStorage.setItem(ACTIVITY_STORAGE_KEY, now);
        localStorage.setItem(SESSION_START_KEY, now);
      } catch {
        // ignore
      }
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    setLoading(true);
    try {
      const googleUser = await signInWithGoogle();
      setUser(googleUser);
      const now = String(Date.now());
      try {
        localStorage.setItem(ACTIVITY_STORAGE_KEY, now);
        localStorage.setItem(SESSION_START_KEY, now);
      } catch {
        // ignore
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendVerificationEmail = async () => {
    await sendVerificationEmail();
  };

  const handleReloadUser = async () => {
    const refreshedUser = await reloadUser();
    if (refreshedUser) {
      setUser(refreshedUser);
    }
    return refreshedUser;
  };

  const handleResetPassword = async (targetEmail: string) => {
    await resetPassword(targetEmail);
  };

  const logout = async () => {
    try {
      try {
        localStorage.removeItem(ACTIVITY_STORAGE_KEY);
        localStorage.removeItem(SESSION_START_KEY);
      } catch {
        // ignore
      }
      await signOutUser();
      setUser(null);
      setToken(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isAdmin = Boolean(
    user &&
    user.emailVerified && (
      user.role === 'admin' ||
      isAdminUser(user, token) ||
      isKnownAdminEmail(user.email)
    )
  );
  const role: UserRole = isAdmin ? 'admin' : (user?.role || 'user');

  const value: AuthContextType = {
    isAuthenticated: Boolean(user),
    isAdmin,
    user,
    token,
    role,
    login,
    signUp,
    loginWithGoogle,
    sendVerificationEmail: handleSendVerificationEmail,
    reloadUser: handleReloadUser,
    resetPassword: handleResetPassword,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
